import React, { useState, useEffect } from 'react';
import './Dashboard.css';

const API_URL = 'https://mega-brasil-api.cejusc.workers.dev';

function Dashboard({ user, onLogout }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    currentPassword: '',
    newPassword: ''
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({ ...formData, name: data.user.name });
      }
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
    }
    setLoading(false);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const body = {};
      
      if (formData.name !== profile.user.name) {
        body.name = formData.name;
      }
      
      if (formData.currentPassword && formData.newPassword) {
        body.currentPassword = formData.currentPassword;
        body.newPassword = formData.newPassword;
      }

      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        await loadProfile();
        setEditMode(false);
        setFormData({ ...formData, currentPassword: '', newPassword: '' });
        alert('Perfil atualizado com sucesso!');
      } else {
        const data = await response.json();
        alert(data.error || 'Erro ao atualizar perfil');
      }
    } catch (error) {
      alert('Erro ao atualizar perfil');
    }
    setLoading(false);
  };

  if (loading && !profile) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <aside className="dashboard-sidebar">
        <div className="user-info">
          <div className="user-avatar">
            {profile?.user?.profilePicture ? (
              <img src={profile.user.profilePicture} alt={profile.user.name} />
            ) : (
              <div className="avatar-placeholder">
                {profile?.user?.name?.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <h3>{profile?.user?.name}</h3>
          <p className="user-email">{profile?.user?.email}</p>
          {profile?.user?.premium && <span className="premium-badge">Premium</span>}
        </div>

        <nav className="dashboard-nav">
          <button 
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            📊 Visão Geral
          </button>
          <button 
            className={activeTab === 'combinations' ? 'active' : ''}
            onClick={() => setActiveTab('combinations')}
          >
            🎲 Minhas Combinações
          </button>
          <button 
            className={activeTab === 'statistics' ? 'active' : ''}
            onClick={() => setActiveTab('statistics')}
          >
            📈 Estatísticas
          </button>
          <button 
            className={activeTab === 'profile' ? 'active' : ''}
            onClick={() => setActiveTab('profile')}
          >
            ⚙️ Configurações
          </button>
          <button className="logout-btn" onClick={onLogout}>
            🚪 Sair
          </button>
        </nav>
      </aside>

      <main className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="tab-content">
            <h2>Bem-vindo ao seu Dashboard!</h2>
            
            <div className="stats-cards">
              <div className="stat-card">
                <h3>Total de Combinações</h3>
                <p className="stat-number">{profile?.stats?.totalCombinations || 0}</p>
                <p className="stat-label">Geradas até agora</p>
              </div>
              
              <div className="stat-card">
                <h3>Membro desde</h3>
                <p className="stat-text">
                  {profile?.user?.memberSince 
                    ? new Date(profile.user.memberSince).toLocaleDateString('pt-BR')
                    : 'N/A'}
                </p>
              </div>
              
              <div className="stat-card">
                <h3>Status da Conta</h3>
                <p className="stat-text">
                  {profile?.user?.verified ? '✅ Verificada' : '⚠️ Pendente verificação'}
                </p>
              </div>
              
              <div className="stat-card">
                <h3>Último Acesso</h3>
                <p className="stat-text">
                  {profile?.user?.lastLogin 
                    ? new Date(profile.user.lastLogin).toLocaleString('pt-BR')
                    : 'Agora'}
                </p>
              </div>
            </div>

            <div className="quick-actions">
              <h3>Ações Rápidas</h3>
              <div className="action-buttons">
                <button className="action-btn primary">
                  🎲 Gerar Nova Combinação
                </button>
                <button className="action-btn">
                  📊 Ver Estatísticas Completas
                </button>
                {!profile?.user?.premium && (
                  <button className="action-btn premium">
                    ⭐ Tornar-se Premium
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'combinations' && (
          <div className="tab-content">
            <h2>Minhas Combinações</h2>
            <div className="empty-state">
              <p>📝 Histórico de combinações em breve!</p>
              <p>Aqui você verá todas as suas combinações anteriores.</p>
            </div>
          </div>
        )}

        {activeTab === 'statistics' && (
          <div className="tab-content">
            <h2>Estatísticas Avançadas</h2>
            <div className="empty-state">
              <p>📈 Análises estatísticas em desenvolvimento!</p>
              <p>Em breve você terá acesso a gráficos e insights personalizados.</p>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="tab-content">
            <h2>Configurações do Perfil</h2>
            
            {!editMode ? (
              <div className="profile-view">
                <div className="profile-field">
                  <label>Nome:</label>
                  <p>{profile?.user?.name}</p>
                </div>
                <div className="profile-field">
                  <label>Email:</label>
                  <p>{profile?.user?.email}</p>
                </div>
                <div className="profile-field">
                  <label>Status:</label>
                  <p>{profile?.user?.verified ? 'Email verificado ✅' : 'Email não verificado ⚠️'}</p>
                </div>
                <button className="btn-edit" onClick={() => setEditMode(true)}>
                  ✏️ Editar Perfil
                </button>
              </div>
            ) : (
              <form onSubmit={handleUpdateProfile} className="profile-form">
                <div className="form-group">
                  <label>Nome:</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                
                <h3>Alterar Senha (opcional)</h3>
                <div className="form-group">
                  <label>Senha Atual:</label>
                  <input
                    type="password"
                    value={formData.currentPassword}
                    onChange={(e) => setFormData({...formData, currentPassword: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Nova Senha:</label>
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                    minLength={8}
                  />
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="btn-save" disabled={loading}>
                    {loading ? 'Salvando...' : '💾 Salvar'}
                  </button>
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => {
                      setEditMode(false);
                      setFormData({ 
                        name: profile.user.name, 
                        currentPassword: '', 
                        newPassword: '' 
                      });
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;